import { MapV2Button } from 'components/mapV2/elements/MapV2Button';
import { useMapV2LightGallery } from 'components/mapV2/hooks/MapV2LightGallery';
import { hexToRGB } from 'components/mapV2/utils/utils';
import { EFonts } from 'libs/styles/styling';
import { getSmallerImage } from 'libs/utils/imageUtils/imageUtils';
import { useImageColors } from 'libs/utils/imageUtils/useImageColor';
import { Localization } from 'static/localization/localization';
import styled from 'styled-components/macro';

interface Props {
    className?: string;
    imageUrl: string;
    title: string;
    googleMapCoordinates: {
        lat: number;
        lng: number;
    };
    imageStyleId?: string;
}

export const ContentHeader = (props: Props): JSX.Element => {
    const lightGallery = useMapV2LightGallery({ imageUrls: [props.imageUrl] });

    const gradientColor = useImageColors(getSmallerImage(props.imageUrl, 400));

    function openGoogleMaps() {
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${props.googleMapCoordinates.lat},${props.googleMapCoordinates.lng}`
        );
    }

    const backgroundSize = props.imageStyleId === 'stretch' ? 'cover' : 'contain';

    return (
        <S_ContentHeader className={props.className}>
            {lightGallery.element}
            {gradientColor && <S_HeaderGradient $color={gradientColor} />}
            <S_Content>
                {props.imageUrl && (
                    <S_ImageContainer>
                        <S_Image
                            $url={getSmallerImage(props.imageUrl, 400)}
                            onClick={() => lightGallery.open(0)}
                            $backgroundSize={backgroundSize}
                        />
                    </S_ImageContainer>
                )}
                {props.title && <S_Title>{props.title}</S_Title>}
                {props.googleMapCoordinates && (
                    <S_GoogleMapButton
                        label={Localization.MAP_V2_GOOGLE_MAPS_NAVIGATION}
                        iconSrc="https://dvtqxks0zalev.cloudfront.net/5770e310-4259-4eaa-a155-e3519694dc3f.png"
                        onClick={openGoogleMaps}
                    />
                )}
            </S_Content>
        </S_ContentHeader>
    );
};

const S_ContentHeader = styled.div`
    padding: 25px 20px 0 20px;
    position: relative;
`;

const S_HeaderGradient = styled.div<{ $color: string }>`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 450px;
    max-height: 100%;
    background: #eff5f6;
    background: linear-gradient(180deg, ${(props) => hexToRGB(props.$color, 0.8)}, #eff5f6 100%);
    border-radius: 0 0 0 0;
`;

const S_Content = styled.div`
    position: relative;
    z-index: 1;
`;

const S_ImageContainer = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    justify-content: center;
    margin-bottom: 20px;
`;

const S_Image = styled.div<{ $url: string; $backgroundSize: 'cover' | 'contain' }>`
    background-image: url(${(props) => props.$url});
    background-size: ${(props) => props.$backgroundSize};
    background-repeat: no-repeat;
    background-position: center;
    border-radius: 10px;
    height: 200px;
    width: 100%;
    cursor: pointer;
    background-color: white;
`;

const S_Title = styled.div`
    font-size: 20px;
    line-height: 32px;
    color: #2a2a2d;
    font-family: ${EFonts.InterBold};
`;

const S_GoogleMapButton = styled(MapV2Button)`
    margin-top: 20px;
`;
